import prepareTranslate from '@/helpers/dictionary';
import Table from '@/components/Table/index';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import {
  IconEdit,
  IconEye,
  IconX,
  IconPlus,
} from '@tabler/icons-react';
import { Button, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import validations from '@/helpers/validation/index';
import agencyCommissionsMutation from '@/api/mutitations/agencyCommissions';
import docsMutation from '@/api/mutitations/docs';
import PageTemplate from '@/components/PageTemplate';
import { extractFilterOptions, initialColumnFilters } from '@/components/Table/Table.functions';
import { useDisclosure } from '@mantine/hooks';
import Modal from '@/components/Modal/Index';
import DetailsModal from '@/components/DetailsModal';
import AgencyCommissionsCreate from '../../components/AgencyCommissionsCreate';
import AgencyCommissionsView from '../../components/AgencyCommissionsView';
import AgencyCommissionsEdit from '../../components/AgencyCommissionsEdit';

function AgencyCommissions() {
  const t = prepareTranslate();
  const { mutateAsync: agencyCommissionMutate } = agencyCommissionsMutation.useGetAll();
  const { mutateAsync: agencyCommissionDeleteMutate } = agencyCommissionsMutation.useDeleteMany();
  const { mutateAsync: exportFileMutate } = docsMutation.useExportFile();
  const [filterOptions, setFilterOptions] = useState({});

  const [createOpened, { open: createOpen, close: createClose }] = useDisclosure(false);
  const [viewOpened, { open: viewOpen, close: viewClose }] = useDisclosure(false);
  const [updateOpened, { open: updateOpen, close: updateClose }] = useDisclosure(false);
  const [deleteOpened, { open: deleteOpen, close: deleteClose }] = useDisclosure(false);
  const [selectedForDelete, setSelectedForDelete] = useState(null);

  const [selectedAgencyCommision, setselectedAgencyCommision] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewLoadingId, setViewLoadingId] = useState(null);

  const form = useForm({
    validate: {
      deletionReason: (value) => validations.required(value),
    },
  });

  const getData = async (start, end, query, sort) => {
    let result = {};
    try {
      const agentCommissionResult = await agencyCommissionMutate({
        start,
        end,
        query,
        sort,
      });

      const newFilterOptions = extractFilterOptions(agentCommissionResult.data, mockColumns);
      setFilterOptions(newFilterOptions);

      const returnData = agentCommissionResult.data.map((agencyCommissions) => ({
        id: agencyCommissions._id,
        company: agencyCommissions.company,
        product: agencyCommissions.product.map((item) => item.hebrew).join(', '),
        agencyFeeMonthlyDeposit: agencyCommissions.agencyFeeMonthlyDeposit,
        agencyFeeMonthlyAccumulation: agencyCommissions.agencyFeeMonthlyAccumulation,
        agencyFeeAnnualAccumulation: (() => {
          const tiers = agencyCommissions.agencyFeeAnnualAccumulation;
          if (!Array.isArray(tiers) || !tiers.length) return '-';
          if (tiers.length === 1) return tiers[0].rate;
          return `${tiers.length} מדרגות`;
        })(),
        agencyFeeAnnualDeposit: (() => {
          const tiers = agencyCommissions.agencyFeeAnnualDeposit;
          if (!Array.isArray(tiers) || !tiers.length) return '-';
          if (tiers.length === 1) return tiers[0].rate;
          return `${tiers.length} מדרגות`;
        })(),
        agentNumber: agencyCommissions.agentNumber,
        payeeFeesStartDate: agencyCommissions.payeeFeesStartDate,
        volumeFeesStartDate: agencyCommissions.volumeFeesStartDate,
        isPayeeFeesVatIncluded: agencyCommissions.isPayeeFeesVatIncluded,
        isVolumeFeesVatIncluded: agencyCommissions.isVolumeFeesVatIncluded,
        files: agencyCommissions.files,
      }));

      agentCommissionResult.data = returnData;

      result = agentCommissionResult;
    } catch (error) {
      notifications.show({
        title: t('pages.agencyCommissions.notification.errors.find.title'),
        message: t('pages.agencyCommissions.notification.errors.find.message'),
      });
      // eslint-disable-next-line no-console
      console.error('Error fetching agency commissions:', error);
    }

    return result;
  };

  const handleClickOnView = (id) => {
    setselectedAgencyCommision(id);
    setViewLoadingId(id);
    viewOpen();
  };

  const handleClickOnEdit = (id) => {
    setselectedAgencyCommision(id);
    updateOpen();
  };

  // eslint-disable-next-line consistent-return
  const createFile = async (option, query) => {
    const requestColumns = mockColumns.filter((column) => column.title !== 'פעולות');
    const exportData = {
      query,
      fileType: option,
      service: 'agencyCommissions',
      columns: requestColumns,
      titleArray: ['personalDetails.fullName'],
    };

    const fileTitleBase = t('pages.agencyCommissions.fileTitle');
    const fileTitle = option === 'pdf' ? `${fileTitleBase}.pdf` : `${fileTitleBase}.xlsx`;

    try {
      const fileData = await exportFileMutate(exportData);
      return { fileData, fileTitle };
    } catch (error) {
      notifications.show({
        title: t('pages.agencyCommissions.notification.errors.export.title'),
        message: t('pages.agencyCommissions.notification.errors.export.message'),
      });
      // eslint-disable-next-line no-console
      console.error('Error fetching users:', error);
    }
  };

  const actionResultClose = () => setActionResultOpened(false);
  const actionResultOpen = () => setActionResultOpened(true);
  const [actionResultOpened, setActionResultOpened] = useState(false);
  const [resultLabel, setResultLabel] = useState('');
  const [resultActions, setResultActions] = useState([]);

  const handleModalClose = () => {
    actionResultClose();
  };

  const sucessResultActions = [
    <Button key='closeButton' onClick={handleModalClose}>{t('global.close')}</Button>,
  ];

  const faildResultActions = [
    <Button key='closeButton' onClick={() => actionResultClose()}>{t('global.close')}</Button>,
  ];

  const handleClickOnDelete = async () => {
    form.validate();

    try {
      await agencyCommissionDeleteMutate({ data: selectedForDelete });
      setResultLabel(t('pages.agencyCommissions.succesdDelete'));
      setResultActions(sucessResultActions);
      actionResultOpen();
      setSelectedForDelete(null);
      deleteClose();
      form.reset();
      setRefreshKey((prev) => prev + 1);
    } catch {
      setResultLabel(t('pages.agencyCommissions.faildDelete'));
      setResultActions(faildResultActions);
      actionResultOpen();
    }
  };

  const mockColumns = [
    {
      accessor: 'company.hebrew',
      title: 'חברה',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'product',
      title: 'מוצר',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'agentNumber',
      title: 'מספר סוכן',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'agencyFeeMonthlyDeposit',
      title: 'נפרעים מהפקדה',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'agencyFeeMonthlyAccumulation',
      title: 'נפרעים מצבירה',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'agencyFeeAnnualDeposit',
      title: 'היקף מהפקדה',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'agencyFeeAnnualAccumulation',
      title: 'היקף מצבירה',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'actions',
      title: 'פעולות',
      actions: [
        {
          title: 'צפייה',
          action: handleClickOnView,
          icon: <IconEye />,
          showLabel: true,
          isPrimary: true,
          loading: (id) => viewLoadingId === id,
        },
        {
          title: 'עריכה',
          action: handleClickOnEdit,
          icon: <IconEdit />,
          showLabel: true,
          isPrimary: true,
        },
      ],
    },
  ];

  const mockSelectionActions = [
    {
      title: 'העבר לארכיון',
      icon: <IconX stroke='1' />,
      onClick: (data) => {
        setSelectedForDelete(data);
        deleteOpen();
      },
    },
  ];

  const exportOptions = ['excel'];

  const handleCreateClick = () => {
    createOpen();
  };

  const initialColumnFilters2 = initialColumnFilters(mockColumns);
  const [columnFilters, setColumnFilters] = useState(initialColumnFilters2);

  const deleteActions = [
    <Button key='delete' w={'6rem'} onClick={form.onSubmit(handleClickOnDelete)}>
      {t('global.delete')}
    </Button>,
    <Button key='cancel' w={'6rem'} onClick={() => deleteClose()} variant='outline'>
      {t('global.cancel')}
    </Button>,
  ];

  return (
    <PageTemplate title={t('pages.agencyCommissions.title')}
      actions={[
        <Button key='addButton' onClick={handleCreateClick} leftSection={<IconPlus />}>
          {t('pages.agencyCommissions.action')}
        </Button>,
      ]}>
      <Table
        key={refreshKey}
        isExportActive
        columns={mockColumns}
        createFile={createFile}
        getData={getData}
        selectionActions={mockSelectionActions}
        isColumnsActive={false}
        exportOptions={exportOptions}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        filterOptions={filterOptions}
        showSelectionColumn = {true}
      />
      <AgencyCommissionsCreate
        open={createOpen}
        opened={createOpened}
        close={createClose}
        setKey={setRefreshKey}
      />
      <AgencyCommissionsView
        open={viewOpen}
        opened={viewOpened}
        close={viewClose}
        id={selectedAgencyCommision}
        onReady={() => setViewLoadingId(null)}
      />
      <AgencyCommissionsEdit
        open={updateOpen}
        opened={updateOpened}
        close={updateClose}
        id={selectedAgencyCommision}
        setKey={setRefreshKey}
      />
      <Modal
        opened={actionResultOpened}
        onClose={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />
      <DetailsModal
        opened={deleteOpened}
        close={deleteClose}
        label={t('pages.agencyCommissions.verifyDeletion')}
        actions={deleteActions}
      >
        <TextInput
          required
          {...form.getInputProps('deletionReason')}
          label={t('pages.agencyCommissions.deletionReason')}
        />
      </DetailsModal>
    </PageTemplate>
  );
}

export default AgencyCommissions;
